import { useEffect, useMemo, useState } from 'react'
import { FiX, FiSettings, FiExternalLink, FiSave, FiLoader, FiRefreshCw } from 'react-icons/fi'
import { useLanguageStore } from '../../store/languageStore'
import { loc, locLabel } from '../../utils/localized'
import { useOpenSettings } from '../../hooks/useOpenSettings'
import { usePageSettings } from '../../hooks/usePageSettings'
import { getItemById, type SettingsItem } from '../../data/settingsRegistry'
import type { PageSettingsDef, PageSettingField } from '../../data/pageSettingsRegistry'
import type { PageSettingsValues } from '../../api/pageSettingsApi'

interface PageSettingsPanelProps {
  def: PageSettingsDef
  open: boolean
  onClose: () => void
}

function ToggleRow({
  label,
  description,
  value,
  onChange,
  disabled,
  isRTL,
}: {
  label: string
  description?: string
  value: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  isRTL: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{label}</div>
        {description && <p className="mt-0.5 text-xs text-slate-500">{description}</p>}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(!value)}
        className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
          value ? 'bg-teal-600' : 'bg-slate-300 dark:bg-slate-600'
        }`}
        aria-pressed={value}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            value ? (isRTL ? '-translate-x-6' : 'translate-x-6') : isRTL ? '-translate-x-1' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )
}

function NumberRow({
  label,
  description,
  value,
  min,
  max,
  onChange,
  disabled,
}: {
  label: string
  description?: string
  value: number
  min?: number
  max?: number
  onChange: (next: number) => void
  disabled?: boolean
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-2 text-sm font-medium text-slate-800 dark:text-slate-100">{label}</div>
      {description && <p className="mb-2 text-xs text-slate-500">{description}</p>}
      <input
        type="number"
        disabled={disabled}
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const n = Number(e.target.value)
          if (!Number.isFinite(n)) return
          onChange(n)
        }}
        className="input-field text-sm"
      />
    </div>
  )
}

function TextRow({
  label,
  description,
  value,
  onChange,
  disabled,
}: {
  label: string
  description?: string
  value: string
  onChange: (next: string) => void
  disabled?: boolean
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-2 text-sm font-medium text-slate-800 dark:text-slate-100">{label}</div>
      {description && <p className="mb-2 text-xs text-slate-500">{description}</p>}
      <input
        type="text"
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-field text-sm"
      />
    </div>
  )
}

function groupFields(fields: PageSettingField[], language: string) {
  const map = new Map<string, { title: string; fields: PageSettingField[] }>()
  for (const f of fields) {
    const title = f.section ? locLabel(language, f.section) : loc(language, 'عام', 'General', 'Général')
    if (!map.has(title)) map.set(title, { title, fields: [] })
    map.get(title)!.fields.push(f)
  }
  return [...map.values()]
}

export default function PageSettingsPanel({ def, open, onClose }: PageSettingsPanelProps) {
  const { language } = useLanguageStore()
  const isRTL = language === 'ar'
  const t = (ar: string, en: string, fr?: string) => loc(language, ar, en, fr)
  const { openSettings, openItem } = useOpenSettings()
  const { values, defaults, isLoading, isSaving, save } = usePageSettings(def)
  const [draft, setDraft] = useState<PageSettingsValues>(values)

  useEffect(() => {
    setDraft(values)
  }, [values, def.pageId])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const related: SettingsItem[] = useMemo(() => {
    return (def.relatedItemIds || [])
      .map((id) => getItemById(id))
      .filter((x): x is SettingsItem => !!x)
  }, [def.relatedItemIds])

  const sections = useMemo(
    () => groupFields(def.fields || [], language),
    [def.fields, language]
  )

  const dirty = useMemo(() => {
    const keys = new Set([...Object.keys(draft), ...Object.keys(values)])
    for (const k of keys) {
      if (draft[k] !== values[k]) return true
    }
    return false
  }, [draft, values])

  if (!open) return null

  const title = locLabel(language, def.label)
  const patch = (key: string, value: boolean | number | string) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <div className="fixed inset-0 z-[120] flex" dir={isRTL ? 'rtl' : 'ltr'}>
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
        aria-label={t('إغلاق', 'Close', 'Fermer')}
        onClick={onClose}
      />

      <aside
        className={`relative z-[121] flex h-full w-full max-w-md flex-col shadow-2xl ${
          isRTL ? 'me-auto' : 'ms-auto'
        }`}
        style={{ backgroundColor: 'var(--bg-secondary, #fff)' }}
      >
        <header className="flex items-start gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-700">
          <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-teal-600 text-white">
            <FiSettings className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-slate-900 dark:text-slate-50">
              {t(`إعدادات: ${title}`, `Settings: ${title}`, `Paramètres : ${title}`)}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {t(`إعدادات خاصة بالصفحة · Page.${def.pageId}`, `Page-only settings · Page.${def.pageId}`, `Page-only settings · Page.${def.pageId}`)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <FiX />
          </button>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
          {/* Related central settings */}
          <section>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t('إعدادات مرتبطة', 'Related settings', 'Paramètres associés')}
              </h3>
              <button
                type="button"
                onClick={() => {
                  openSettings({
                    domain: def.domain,
                    context: def.context,
                    pageId: def.pageId,
                  })
                  onClose()
                }}
                className="inline-flex items-center gap-1 text-xs text-teal-700 hover:underline"
              >
                <FiExternalLink className="h-3 w-3" />
                {t('مركز الإعدادات', 'Settings center', 'Centre des paramètres')}
              </button>
            </div>

            {related.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-200 px-3 py-3 text-xs text-slate-500 dark:border-slate-700">
                {t('لا توجد عناصر مرتبطة بعد — يمكنك فتح المجال من مركز الإعدادات.', 'No linked items yet — open the domain in Settings Center.', 'No linked items yet — open the domain in Settings Center.')}
              </p>
            ) : (
              <ul className="space-y-2">
                {related.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        openItem(item.id)
                        onClose()
                      }}
                      className="flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-start text-sm hover:border-teal-300 hover:bg-teal-50/60 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-teal-950/30"
                    >
                      <span className="min-w-0">
                        <span className="block font-medium text-slate-800 dark:text-slate-100">
                          {locLabel(language, item.label)}
                        </span>
                        <span className="block truncate text-xs text-slate-500">
                          {locLabel(language, item.description)}
                        </span>
                      </span>
                      <FiExternalLink className="shrink-0 text-slate-400" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Page-only fields */}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t(`تحكم صفحة ${title}`, `${title} controls`, `Contrôles ${title}`)}
            </h3>

            {isLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <FiLoader className="animate-spin" />
                {t('جاري التحميل…', 'Loading…', 'Chargement…')}
              </div>
            ) : sections.length === 0 ? (
              <p className="text-xs text-slate-500">
                {t('لا توجد حقول خاصة لهذه الصفحة.', 'No page-specific fields.', 'Aucun champ spécifique à cette page.')}
              </p>
            ) : (
              <div className="space-y-4">
                {sections.map((sec) => (
                  <div key={sec.title}>
                    <div className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                      {sec.title}
                    </div>
                    <div className="space-y-2">
                      {sec.fields.map((field) => {
                        const label = locLabel(language, field.label)
                        const description = field.description
                          ? locLabel(language, field.description)
                          : undefined
                        const value = draft[field.key] ?? field.defaultValue

                        if (field.type === 'boolean') {
                          return (
                            <ToggleRow
                              key={field.key}
                              label={label}
                              description={description}
                              value={Boolean(value)}
                              onChange={(v) => patch(field.key, v)}
                              isRTL={isRTL}
                            />
                          )
                        }
                        if (field.type === 'number') {
                          return (
                            <NumberRow
                              key={field.key}
                              label={label}
                              description={description}
                              value={Number(value)}
                              min={field.min}
                              max={field.max}
                              onChange={(v) => patch(field.key, v)}
                            />
                          )
                        }
                        return (
                          <TextRow
                            key={field.key}
                            label={label}
                            description={description}
                            value={String(value ?? '')}
                            onChange={(v) => patch(field.key, v)}
                          />
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <footer className="space-y-2 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!dirty || isSaving}
              onClick={() => save(draft)}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-teal-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-50"
            >
              {isSaving ? <FiLoader className="animate-spin" /> : <FiSave />}
              {t('حفظ', 'Save', 'Enregistrer')}
            </button>
            <button
              type="button"
              disabled={isSaving}
              onClick={() => setDraft({ ...defaults })}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
              title={t('استعادة الافتراضي', 'Reset to defaults', 'Rétablir les valeurs par défaut')}
            >
              <FiRefreshCw />
            </button>
          </div>
          <p className="text-[11px] leading-relaxed text-slate-500">
            {t('هذه الإعدادات تؤثر على هذه الصفحة فقط. الإعدادات العامة تبقى في مركز الإعدادات.', 'These controls affect this page only. Shared settings stay in the Settings Center.', 'These controls affect this page only. Shared settings stay in the Settings Center.')}
          </p>
        </footer>
      </aside>
    </div>
  )
}
